import {
  CompanyType,
  IssueStatus,
  IssueType,
  ObjectType,
  OrderIssueResolutionType,
} from '@planning/app/api'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { RailcarTrackPositionItemStore } from '@planning/rt-stores/railTrack/RailcarTrackPositionItemStore'
import { RailVisitItemStore } from '@planning/rt-stores/railVisit/RailVisitItemStore'
import _ from 'lodash'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import { ContainerJourneyItemStore } from './ContainerJourneyItemStore'
import { IIssueItem } from './IssueItem'
import { IssueItemStore } from './IssueItemStore'

export type IssuesTypeCount = {
  all: number
  unresolved: number
  followUp: number
}

export const requiredTransportDataResolution: OrderIssueResolutionType[] = [
  OrderIssueResolutionType.ChangeInboundTransport,
  OrderIssueResolutionType.ChangeOutboundTransport,
]

// [railcarMissingIssue] TODO: UT this store - filtering, fetching
export class IssuesViewStore {
  issueFilterStatus?: IssueStatus
  issueFilterCompanyType?: CompanyType
  issueFilterObjectType?: ObjectType
  issueFilterType?: IssueType
  issueToBeResolved?: IIssueItem
  carrierNameFilter? = ''
  containerNumberFilter? = ''
  isFilterEnabled = false

  filterByVisitId?: number
  filterByRailcarTrackPositionId?: number

  loadContainerJourneyDataReaction = false

  constructor(
    private issueItemStore: IssueItemStore,
    public containerJourneyItemStore: ContainerJourneyItemStore,
    private containerItemStore: ContainerItemStore,
    private railVisitItemStore: RailVisitItemStore,
    private railcarTrackPositionItemStore: RailcarTrackPositionItemStore,
  ) {
    makeObservable(this, {
      issueFilterStatus: observable,
      issueFilterCompanyType: observable,
      issueFilterType: observable,
      carrierNameFilter: observable,
      containerNumberFilter: observable,
      isFilterEnabled: observable,
      issueToBeResolved: observable,
      issueFilterObjectType: observable,
      filterByVisitId: observable,
      filterByRailcarTrackPositionId: observable,
      loadContainerJourneyDataReaction: observable,

      dataFilteredByIssueStatus: computed,
      filteredData: computed,
      dataFilteredByVisit: computed,
      issuesTypeCount: computed,
      isFilteredByVisit: computed,
      isFilteredByCarrier: computed,
      isFiltered: computed,
      railVisitFilterName: computed,
      railcarTrackPositionFilterName: computed,
      issueTypes: computed,
      issueContainerIds: computed,
      issueOrderIds: computed,

      setIssueToBeResolved: action,
      setContainerNumberFilter: action,
      setIssueFilterStatus: action,
      setIssueFilterType: action,
      setIssueFilterCompanyType: action,
      setIssueFilterObjectType: action,
      setCarrierNameFilter: action,
      enableFilters: action,
      setFilterByVisitId: action,
      setFilterByRailCarTrackPositionId: action,
      setLoadContainerJourneyDataReaction: action,
      clearFilters: action,
    })

    reaction(
      () => {
        return {
          loadJourneyData: this.loadContainerJourneyDataReaction,
          ids: this.issueOrderIds,
        }
      },
      react => {
        if (react.loadJourneyData && react.ids) {
          this.containerJourneyItemStore.fetchByOrderIds(react.ids)
        }
      },
    )

    reaction(
      () => {
        return {
          loadJourneyData: this.loadContainerJourneyDataReaction,
          containerIds: this.issueContainerIds,
        }
      },
      react => {
        if (react.loadJourneyData && react.containerIds) {
          this.containerItemStore.fetchByIds(react.containerIds) //TODO: this is a workaround, we need the containerJourneyDataStore to get Containers from the ContainerItemStore instead of the ContainerService
        }
      },
    )
  }

  get railVisitFilterName() {
    return (
      _(this.railVisitItemStore.elements).find(v => v.id === this.filterByVisitId)?.data.name ?? ''
    )
  }

  get railcarTrackPositionFilterName() {
    return (
      _(this.railcarTrackPositionItemStore.elements).find(
        r => r.id === this.filterByRailcarTrackPositionId,
      )?.data.railcarName ?? ''
    )
  }

  get issueOrderIds(): number[] {
    return this.issueItemStore.notResolvedItemsWithObjectTypeOrder.map(i => i.data.objectId)
  }

  get issueContainerIds(): number[] {
    return this.issueItemStore.notResolvedItemsWithObjectTypeContainer.map(i => i.data.objectId)
  }

  setIssueToBeResolved(issue?: IIssueItem) {
    this.issueToBeResolved = issue
  }

  setContainerNumberFilter(filter?: string) {
    this.containerNumberFilter = filter
  }

  setCarrierNameFilter(filter?: string) {
    this.carrierNameFilter = filter
  }

  enableFilters(apply: boolean) {
    this.isFilterEnabled = apply
  }

  fetch = async () => {
    await this.issueItemStore.fetch()
  }

  setIssueFilterStatus(issueFilterType?: IssueStatus) {
    if (this.issueFilterStatus !== issueFilterType) {
      this.issueFilterStatus = issueFilterType
    }
  }

  setIssueFilterType(issueFilterType?: string) {
    if (this.issueFilterType !== issueFilterType) {
      this.issueFilterType = issueFilterType as IssueType
    }
  }

  setIssueFilterCompanyType(issueFilterType?: CompanyType) {
    if (this.issueFilterCompanyType !== issueFilterType) {
      this.issueFilterCompanyType = issueFilterType
    }
  }

  setIssueFilterObjectType(issueFilterObjectType?: ObjectType) {
    if (this.issueFilterObjectType !== issueFilterObjectType) {
      this.issueFilterObjectType = issueFilterObjectType
    }
  }

  setFilterByVisitId(visitId?: number) {
    this.filterByVisitId = visitId
  }

  setFilterByRailCarTrackPositionId(railCarTrackPositionId?: number) {
    this.filterByRailcarTrackPositionId = railCarTrackPositionId
  }

  setLoadContainerJourneyDataReaction(value: boolean) {
    this.loadContainerJourneyDataReaction = value
  }

  clearFilters() {
    this.setCarrierNameFilter()
    this.setContainerNumberFilter()
    this.setFilterByVisitId()
    this.setFilterByRailCarTrackPositionId()
    this.enableFilters(false)
    this.clearChips()
  }

  clearChips() {
    this.setIssueFilterStatus()
    this.setIssueFilterType()
  }

  get isFilteredByVisit(): boolean {
    return !!this.filterByVisitId || !!this.filterByRailcarTrackPositionId
  }

  get isFilteredByCarrier(): boolean {
    return this.isFilterEnabled && !!this.carrierNameFilter
  }

  get isFiltered(): boolean {
    return this.isFilteredByCarrier || this.isFilteredByVisit
  }

  get issuesTypeCount(): IssuesTypeCount {
    const items = this.filteredData
    let unresolved = 0
    let followUp = 0

    items.forEach(item => {
      if (item.data.issueStatus === IssueStatus.Unresolved) {
        unresolved++
      } else if (item.data.issueStatus === IssueStatus.FollowUp) {
        followUp++
      }
    })

    return {
      all: items.length,
      unresolved: unresolved,
      followUp: followUp,
    }
  }

  get dataFilteredByIssueStatus(): IIssueItem[] {
    const data = this.filteredData.filter(
      i =>
        (!this.issueFilterStatus || i.data.issueStatus === this.issueFilterStatus) &&
        (!this.issueFilterType || i.data.issueType === this.issueFilterType) &&
        (!this.issueFilterCompanyType || i.data.companyType === this.issueFilterCompanyType) &&
        (!this.issueFilterObjectType || i.data.objectType === this.issueFilterObjectType),
    )
    return data
  }

  get issueTypes(): string[] {
    return _(this.filteredData)
      .map(i => i.data.issueType)
      .uniq()
      .value()
  }

  get filteredData(): IIssueItem[] {
    let filteredData = this.dataFilteredByVisit.filter(i => i.isValidContainerOrOrderIssue)

    // Filter by container number
    if (this.containerNumberFilter) {
      filteredData = filteredData.filter(i =>
        i.containsContainerNumber(this.containerNumberFilter!),
      )
    }

    // Filter by carrier name
    if (this.carrierNameFilter && this.isFilterEnabled) {
      filteredData = filteredData.filter(i => {
        const { containerJourney } = i
        const filter = this.carrierNameFilter!.toLocaleUpperCase()

        return (
          containerJourney &&
          (containerJourney.inboundOrder?.visit?.data.identifier
            ?.toLocaleUpperCase()
            .includes(filter) ||
            containerJourney.outboundOrder?.visit?.data.identifier
              ?.toLocaleUpperCase()
              .includes(filter))
        )
      })
    }

    return filteredData
  }

  get dataFilteredByVisit() {
    let data = [
      ...this.issueItemStore.notResolvedItemsWithObjectTypeOrder,
      ...this.issueItemStore.notResolvedItemsWithObjectTypeContainer,
    ]

    // Filter by VisitId
    if (this.filterByVisitId) {
      data = data.filter(i => {
        const { containerJourney } = i
        return (
          containerJourney &&
          (containerJourney.inboundOrder?.visit?.id === this.filterByVisitId ||
            containerJourney.outboundOrder?.visit?.id === this.filterByVisitId)
        )
      })
    }

    // Filter by RailCarTrackPositionId
    if (this.filterByRailcarTrackPositionId) {
      data = data.filter(i => {
        const { containerJourney } = i

        return (
          containerJourney &&
          (containerJourney.inboundOrder?.data?.railcarTrackPositionId ===
            this.filterByRailcarTrackPositionId ||
            containerJourney.outboundOrder?.data?.railcarTrackPositionId ===
              this.filterByRailcarTrackPositionId)
        )
      })
    }

    return data
  }
}
